import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, alpha, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Snackbar, Stack, TextField, Typography } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from 'api/client';
import { useAuth } from 'context/AuthContext';
import { correlateIntelFeed, syncTaxiiFeed } from 'api/securityOps';
import { GREEN, MONO, ago } from './scanUi';
import { FILTERS, SEV, affected, search, summary, vendors, withMine } from './intelLogic';

const panel = { borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper' };
const fail = (e, fb) => e?.response?.data?.detail || e?.message || fb;
const Pill = ({ color, children }) => <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, px: 1.3, py: 0.5, borderRadius: 99, bgcolor: alpha(color, 0.16), color, whiteSpace: 'nowrap' }}>{children}</Typography>;
const SHOWN = 30;

export default function IntelFeedsPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [rows, setRows] = useState([]);
  const [correlations, setCorrelations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [vendor, setVendor] = useState('');
  const [q, setQ] = useState('');
  const [shown, setShown] = useState(SHOWN);
  const [busy, setBusy] = useState(false);
  const [taxii, setTaxii] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    try {
      const [{ data }, cor] = await Promise.all([api.get('/api/intel-feeds/?limit=500'), correlateIntelFeed({ days: 365, limit: 300 }).catch(() => ({ results: [] }))]);
      setRows(Array.isArray(data) ? data : data?.results || []);
      setCorrelations(cor?.results || []);
    } catch (e) { setToast({ severity: 'error', message: fail(e, 'Could not load the intel feed.') }); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => setShown(SHOWN), [filter, vendor, q]);

  const all = useMemo(() => withMine(rows, correlations), [rows, correlations]);
  const hit = useMemo(() => affected(correlations), [correlations]);
  const sum = useMemo(() => summary(all), [all]);
  const tops = useMemo(() => vendors(all), [all]);
  const now = new Date();
  const f = FILTERS.find((x) => x.id === filter);
  const list = useMemo(() => search(all.filter((r) => f.test(r, now) && (!vendor || r.vendor === vendor)), q), [all, f, vendor, q]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = async () => {
    setBusy(true);
    try { await api.post('/api/intel-feeds/refresh/', {}); await load(); setToast({ severity: 'success', message: 'The list was refreshed.' }); } catch (e) { setToast({ severity: 'error', message: fail(e, 'Could not refresh.') }); }
    setBusy(false);
  };
  const syncTaxii = async () => {
    setBusy(true);
    try { await syncTaxiiFeed({ objects_url: taxii.url, api_key: taxii.key, source: taxii.source || undefined }); setTaxii(null); await load(); setToast({ severity: 'success', message: 'The extra feed was synchronised.' }); } catch (e) { setToast({ severity: 'error', message: fail(e, 'Could not synchronise that feed.') }); }
    setBusy(false);
  };
  const stat = (label, value, sub, color) => (
    <Box sx={{ ...panel, p: 2.2, flex: '1 1 190px' }}>
      <Typography variant="overline" color="text.secondary">{label}</Typography>
      <Typography sx={{ fontSize: 28, fontWeight: 700, fontFamily: MONO, color }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{sub}</Typography>
    </Box>
  );

  if (loading) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress sx={{ color: GREEN }} /></Box>;
  return (
    <Stack spacing={2.5} className="fuzzscan-page" sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1.5}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Intel feeds</Typography>
          <Typography color="text.secondary" variant="body2">Flaws attackers are using right now, with the ones in your own findings first</Typography>
        </Box>
        {isAdmin && <Stack direction="row" gap={1}><Button variant="outlined" color="inherit" disabled={busy} onClick={refresh}>Refresh now</Button><Button variant="outlined" color="inherit" onClick={() => setTaxii({ url: '', key: '', source: '' })}>Add a TAXII feed</Button></Stack>}
      </Stack>

      {hit.length > 0 && (
        <Box sx={{ p: 3, borderRadius: 3, border: 1, borderColor: alpha('#ef4444', 0.45), background: `linear-gradient(135deg, ${alpha('#ef4444', 0.12)}, transparent 65%)`, bgcolor: 'background.paper' }}>
          <Typography variant="overline" sx={{ fontWeight: 700, color: '#f87171' }}>Affects you</Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 700 }}>{hit.length} of your open findings {hit.length === 1 ? 'is' : 'are'} on the exploited list</Typography>
          {hit.slice(0, 4).map((c) => (
            <Stack key={c.finding_id} direction="row" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ mt: 1.4, p: 1.4, borderRadius: 2, bgcolor: alpha('#000', 0.2), border: 1, borderColor: 'divider' }}>
              <Typography sx={{ flex: '1 1 240px', fontWeight: 600 }}>{c.finding_title}</Typography>
              <Typography variant="caption" sx={{ fontFamily: MONO }} color="text.secondary">{c.target}</Typography>
              <Typography variant="caption" sx={{ fontFamily: MONO, fontWeight: 700 }}>{c.cve_id}</Typography>
              <Button size="small" variant="contained" color="success" onClick={() => nav('/remediation')}>Fix it</Button>
            </Stack>
          ))}
          {hit.length > 4 && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>and {hit.length - 4} more in the Remediation center.</Typography>}
        </Box>
      )}

      <Stack direction="row" gap={2} flexWrap="wrap">
        {stat('Exploited flaws', sum.exploited, `of ${sum.shown} newest entries`, '#f87171')}
        {stat('Added this month', sum.month, 'new')}
        {stat('Newest entry', sum.newest ? ago(sum.newest) : '–', 'refreshes every 6 hours')}
      </Stack>

      {tops.length > 0 && (
        <Stack direction="row" gap={1.2} flexWrap="wrap" alignItems="center">
          <Typography variant="overline" color="text.secondary">Most exploited vendors</Typography>
          {tops.map((v) => (
            <Button key={v.vendor} size="small" onClick={() => setVendor(vendor === v.vendor ? '' : v.vendor)} sx={{ textTransform: 'none', borderRadius: 99, px: 1.8, fontWeight: 600, border: 1, borderColor: vendor === v.vendor ? GREEN : 'divider', color: vendor === v.vendor ? GREEN : 'text.secondary' }}>{v.vendor} · {v.n}</Button>
          ))}
        </Stack>
      )}

      <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
        {FILTERS.map((x) => {
          const on = filter === x.id;
          return <Button key={x.id} onClick={() => setFilter(x.id)} sx={{ borderRadius: 99, px: 2, textTransform: 'none', fontWeight: 600, border: 1, borderColor: on ? 'text.secondary' : 'divider', color: on ? 'text.primary' : 'text.secondary' }}>{x.label} {all.filter((r) => x.test(r, now)).length}</Button>;
        })}
        <TextField size="small" placeholder="Search a CVE, vendor or product" value={q} onChange={(e) => setQ(e.target.value)} sx={{ ml: 'auto', minWidth: 260 }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
      </Stack>

      <Box sx={{ ...panel, overflow: 'hidden' }}>
        {!list.length && <Typography color="text.secondary" sx={{ p: 3 }}>{rows.length ? 'Nothing matches that.' : 'The list is empty for now. It fills in within a few hours.'}</Typography>}
        {list.slice(0, shown).map((r) => (
          <Stack key={r.id} direction="row" alignItems="center" gap={2} flexWrap="wrap" sx={{ px: 2.8, py: 1.7, borderTop: 1, borderColor: 'divider' }}>
            <Box sx={{ flex: '3 1 340px', minWidth: 0 }}>
              <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                <Typography sx={{ fontFamily: MONO, fontWeight: 700, fontSize: 14.5 }}>{r.cve_id || r.title}</Typography>
                <Pill color={SEV[r.severity] || '#64748b'}>{(r.severity || 'info').toUpperCase()}</Pill>
                {r.exploited && <Pill color="#ef4444">EXPLOITED</Pill>}
                {r.mine && <Pill color="#60a5fa">YOU HAVE THIS</Pill>}
              </Stack>
              <Typography variant="body2" sx={{ mt: 0.4 }} color="text.secondary">{[r.vendor, r.product].filter(Boolean).join(' · ')}{r.summary ? ` · ${r.summary}` : ''}</Typography>
              {r.payload?.required_action && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3 }}>What to do: {r.payload.required_action}</Typography>}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ width: 90, textAlign: 'right' }}>{ago(r.published_at)}</Typography>
          </Stack>
        ))}
        {list.length > shown && <Box sx={{ p: 2, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}><Button color="inherit" onClick={() => setShown(shown + SHOWN)}>Show more ({list.length - shown} left)</Button></Box>}
      </Box>
      <Typography variant="body2" color="text.secondary">The list comes from CISA, the US cyber agency, and shows flaws that attackers are using right now. It is refreshed every 6 hours. We compare it with your own open findings.</Typography>

      <Dialog open={Boolean(taxii)} onClose={() => setTaxii(null)} fullWidth maxWidth="sm">
        <DialogTitle>Add a TAXII feed</DialogTitle>
        <DialogContent>
          {taxii && (
            <Stack spacing={1.8} sx={{ pt: 1 }}>
              <Alert severity="info">Adds entries from another threat intelligence source. The address must be public.</Alert>
              <TextField size="small" label="Objects address" placeholder="https://taxii.example.com/api/v21/collections/<id>/objects/" value={taxii.url} onChange={(e) => setTaxii({ ...taxii, url: e.target.value })} />
              <TextField size="small" type="password" label="Access key (optional)" value={taxii.key} onChange={(e) => setTaxii({ ...taxii, key: e.target.value })} autoComplete="new-password" />
              <TextField size="small" label="Name of the source (optional)" value={taxii.source} onChange={(e) => setTaxii({ ...taxii, source: e.target.value })} />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setTaxii(null)}>Cancel</Button>
          <Button variant="contained" color="success" disabled={busy || !taxii?.url.trim()} onClick={syncTaxii}>Synchronise</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}>{toast ? <Alert severity={toast.severity} variant="filled">{toast.message}</Alert> : undefined}</Snackbar>
    </Stack>
  );
}
